import React, { useState, useEffect } from 'react';
import { Tag, X } from 'lucide-react';
import api from '../api';

const TAG_COLORS = ['blue', 'green', 'yellow', 'red', 'purple', 'pink', 'indigo', 'gray'];

const TAG_COLOR_CLASSES = {
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
  purple: 'bg-purple-100 text-purple-800',
  pink: 'bg-pink-100 text-pink-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  gray: 'bg-gray-100 text-gray-800'
};

export default function TagSelector({ applicationId, onUpdate }) {
  const [tags, setTags] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('blue');

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line
  }, [applicationId]);

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/candidate-tags/application/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTags(response.data);
      if (onUpdate) onUpdate(response.data);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const addTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await api.post(`/api/candidate-tags/application/${applicationId}`,
        { tag_name: newTagName, color: selectedColor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTagName('');
      setShowInput(false);
      fetchTags();
    } catch (err) {
      console.error('Error adding tag:', err);
    }
  };

  const removeTag = async (tagId) => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/candidate-tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTags();
    } catch (err) {
      console.error('Error removing tag:', err);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {tags.map(tag => (
        <span
          key={tag.id}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${TAG_COLOR_CLASSES[tag.color] || TAG_COLOR_CLASSES.gray}`}
        >
          {tag.tag_name}
          <button
            onClick={() => removeTag(tag.id)}
            className="hover:opacity-70"
          >
            <X size={14} />
          </button>
        </span>
      ))}

      {showInput ? (
        <div className="inline-flex items-center gap-2 border rounded px-2 py-1">
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addTag()}
            className="border-0 outline-none w-24 text-sm"
            placeholder="Tag name"
            autoFocus
          />
          <select
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="text-sm border-0 outline-none"
          >
            {TAG_COLORS.map(color => (
              <option key={color} value={color}>{color}</option>
            ))}
          </select>
          <button onClick={addTag} className="text-green-600 hover:text-green-800 text-sm font-bold">
            ✓
          </button>
          <button onClick={() => setShowInput(false)} className="text-red-600 hover:text-red-800 text-sm font-bold">
            ✕
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowInput(true)}
          className="inline-flex items-center gap-1 px-3 py-1 border border-dashed border-gray-400 rounded-full text-sm text-gray-600 hover:border-gray-600 hover:text-gray-800"
        >
          <Tag size={14} />
          Add Tag
        </button>
      )}
    </div>
  );
}
